import { useEffect, useRef, useState } from 'react';

const DEFAULT_LAT = parseFloat(import.meta.env.VITE_DEFAULT_CITY_LAT) || 40.6405;
const DEFAULT_LNG = parseFloat(import.meta.env.VITE_DEFAULT_CITY_LNG) || -8.6538;

const normalizeHeading = (heading) => {
  const value = Number(heading);
  if (!Number.isFinite(value)) return null;
  return ((value % 360) + 360) % 360;
};

const getScreenAngle = () => {
  if (screen.orientation && Number.isFinite(screen.orientation.angle)) return screen.orientation.angle;
  return Number(window.orientation) || 0;
};

const getDeviceHeading = (event) => {
  if (Number.isFinite(event.webkitCompassHeading)) {
    return normalizeHeading(event.webkitCompassHeading);
  }

  if (!Number.isFinite(event.alpha)) return null;
  return normalizeHeading(360 - event.alpha + getScreenAngle());
};

const getErrorMessage = (err) => {
  if (!err) return 'Não foi possível obter a tua localização.';
  if (err.code === 1) return 'Permissão de localização recusada.';
  if (err.code === 2) return 'A localização está indisponível neste momento.';
  if (err.code === 3) return 'A localização demorou demasiado tempo.';
  return err.message || 'Não foi possível obter a tua localização.';
};

export default function useGeolocation() {
  const [position, setPosition] = useState({ lat: DEFAULT_LAT, lng: DEFAULT_LNG, accuracy: null, heading: null, speed: null });
  const [deviceHeading, setDeviceHeading] = useState(null);
  const [compassPermission, setCompassPermission] = useState('unsupported');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUsingFallback, setIsUsingFallback] = useState(true);
  const watchId = useRef(null);
  const debounce = useRef(null);
  const lastDeviceHeading = useRef(null);
  const compassCleanup = useRef(null);

  const applyPosition = (pos) => {
    setPosition({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      heading: Number.isFinite(pos.coords.heading) ? pos.coords.heading : null,
      speed: Number.isFinite(pos.coords.speed) ? pos.coords.speed : null,
    });
    setIsLoading(false);
    setIsUsingFallback(false);
    setError(null);
  };

  const requestCurrentPosition = () => {
    if (!navigator.geolocation) {
      setError('O teu navegador não suporta localização.');
      setIsLoading(false);
      setIsUsingFallback(true);
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      applyPosition,
      (err) => {
        setError(getErrorMessage(err));
        setIsLoading(false);
        setIsUsingFallback(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
  };

  const startCompass = () => {
    if (compassCleanup.current) return;

    const handleOrientation = (event) => {
      const nextHeading = getDeviceHeading(event);
      if (nextHeading == null) return;

      const previous = lastDeviceHeading.current;
      if (previous != null) {
        const delta = Math.abs(nextHeading - previous);
        if (delta < 1 || delta > 359) return;
      }

      lastDeviceHeading.current = nextHeading;
      setDeviceHeading(nextHeading);
    };

    window.addEventListener('deviceorientationabsolute', handleOrientation, true);
    window.addEventListener('deviceorientation', handleOrientation, true);
    compassCleanup.current = () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  };

  const requestCompassPermission = async () => {
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      setCompassPermission('unsupported');
      return false;
    }

    try {
      if (typeof window.DeviceOrientationEvent.requestPermission === 'function') {
        const result = await window.DeviceOrientationEvent.requestPermission();
        setCompassPermission(result === 'granted' ? 'granted' : 'denied');
        if (result !== 'granted') return false;
      } else {
        setCompassPermission('granted');
      }

      startCompass();
      return true;
    } catch {
      setCompassPermission('denied');
      return false;
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('O teu navegador não suporta localização.');
      setIsLoading(false);
      setIsUsingFallback(true);
      return undefined;
    }

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        clearTimeout(debounce.current);
        debounce.current = setTimeout(() => applyPosition(pos), 500);
      },
      (err) => {
        setError(getErrorMessage(err));
        setIsLoading(false);
        setIsUsingFallback(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );

    return () => {
      if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
      clearTimeout(debounce.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      setCompassPermission('unsupported');
      return undefined;
    }

    if (typeof window.DeviceOrientationEvent.requestPermission === 'function') {
      setCompassPermission('prompt');
      return undefined;
    }

    setCompassPermission('granted');
    startCompass();

    return () => {
      compassCleanup.current?.();
      compassCleanup.current = null;
    };
  }, []);

  const heading = deviceHeading ?? position.heading;

  return {
    ...position,
    heading,
    headingSource: deviceHeading == null ? 'movement' : 'device',
    compassPermission,
    error,
    isLoading,
    isUsingFallback,
    requestCurrentPosition,
    requestCompassPermission,
  };
}
